'use client';

import { Img } from '@/components/builder2/utils/Img';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useCerrarConAtras } from '@/hooks/useCerrarConAtras';
import Link from 'next/link';
import useClientStore from '@/store/useClientStore';
import { getHiddenSystemPages } from '@/lib/hidden-pages';
import { rubroDelPais } from '@/lib/country';
import useThemeStore from '@/store/useThemeStore';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { navigateBuilderCta, resolveNavLink } from '@/utils/functions';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import IndicadorDeNavegacion from '@/components/routing/IndicadorDeNavegacion';

export interface NavLink {
  text: string;
  url: string;
  /**
   * Submenú desplegable del link (ticket #161). Una opción por línea, con el
   * formato `Texto | /url`; las líneas vacías o sin `|` se ignoran.
   *
   * Es texto plano dentro del propio link, así que entra tal cual en el array
   * `links` que el navbar YA guarda: no hay prop nueva en el nodo, no hay
   * formato nuevo de config y no hay nada que migrar. Un navbar sin `submenu`
   * —todos los de hoy— se dibuja exactamente igual que antes.
   */
  submenu?: string;
  /**
   * El mismo submenú ya parseado. Si viene, manda sobre `submenu`. Existe para
   * que el día que el panel ofrezca un repetidor anidado no haya que tocar el
   * renderer.
   */
  children?: NavLink[];
}

export interface BuilderNavbarViewProps {
  links?: NavLink[];
  ctaText?: string;
  ctaUrl?: string;
  bgColor?: string;
  textColor?: string;
  ctaBgColor?: string;
  ctaTextColor?: string;
  logoUrl?: string;
  logoHeight?: number;
  showLogo?: boolean;
  sticky?: boolean;
  transparentOnTop?: boolean;
  fullWidth?: boolean;
  // isEditor=true: dentro del canvas del builder → siempre sólido, sin efecto scroll.
  isEditor?: boolean;
  // fixed=true: lo renderiza el Navbar del layout en las páginas no-home, que
  // esperan una barra fija de 64px (igual que el Navbar genérico que reemplaza).
  fixed?: boolean;
  // Ruta actual para marcar el link activo. Sin esto se lee de window.location
  // (usePathname revienta dentro del Frame de CraftJS).
  pathname?: string;
  innerRef?: (el: HTMLDivElement | null) => void;
}

/** Sun icon */
const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

/** Chevron del submenú (ticket #161). */
const ChevronIcon = ({ abierto }: { abierto: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: abierto ? 'rotate(180deg)' : undefined, transition: 'transform 150ms' }}
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/** Moon icon */
const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

/** Hamburger / X icon */
const MenuIcon = ({ open, color }: { open: boolean; color: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>
    ) : (
      <>
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </>
    )}
  </svg>
);

const hexToRgb = (hex: string): [number, number, number] | null => {
  const c = (hex || '').replace('#', '');
  if (c.length !== 6) return null;
  const n = [c.substring(0, 2), c.substring(2, 4), c.substring(4, 6)].map((p) => parseInt(p, 16));
  return n.some(Number.isNaN) ? null : [n[0], n[1], n[2]];
};

// Luminancia relativa WCAG.
const relativeLuminance = (rgb: [number, number, number]): number => {
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Ratio de contraste WCAG (1 = idénticos, 21 = negro sobre blanco).
const contrastRatio = (fg: string, bg: string): number => {
  const a = hexToRgb(fg);
  const b = hexToRgb(bg);
  if (!a || !b) return 21;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * Navbar del builder, presentacional puro (sin hooks de CraftJS) para poder
 * renderizarlo también fuera del árbol del builder:
 * - En la home lo renderiza el nodo `BuilderNavbar` (wrapper CraftJS).
 * - En el resto de rutas lo renderiza el Navbar del layout con los props que el
 *   dealer configuró en la home (fixed + sólido), para que el navbar se vea IGUAL
 *   en todo el sitio. Mismo patrón que HeroMega/MegaNavbar.
 */
/**
 * POR QUÉ EL CORTE DEL MENÚ ES `xl` (1280px) Y NO `lg` (1024px)
 *
 * Los enlaces del menú de escritorio llevan `whitespace-nowrap` y no encogen:
 * si no entran, desbordan la página entera. El corte era `sm` (640px), que
 * mostraba TODOS los enlaces desde 640px sin mirar cuántos son ni cuánto miden.
 * Los valores de fábrica son 5 enlaces, pero los tenants agregan los suyos.
 * Medido en el sitio publicado con 6 enlaces: 343px de desborde a 640px, 215 a
 * 768, 83 a 900, y recién a ~975 dejaba de desbordar.
 *
 * El 1280 NO es una corazonada, y por favor no lo muevas por una. Sale de medir
 * los 61 navbars realmente publicados (sep-2026, sobre los 83 tenants con
 * sitio). El peor caso real es Comercial Grass y Arueste: 7 enlaces + CTA +
 * toggle de tema, con un logo de 64px de alto. Necesita 1025px sobre los 1216px
 * que da la fila (`max-w-7xl` menos `lg:px-8` de cada lado), o sea que despliega
 * desde ~1095px de viewport y le sobran 191px: unos dos enlaces más de margen.
 *
 * `lg` (1024px) NO alcanza: ese mismo tenant necesita 1095px y desbordaria.
 *
 * Antes de tocar este numero, corre `npm run test:navbar-sweep` en el admin:
 * barre 320 a 1920 al pixel y falla si alguna barra desborda o corta un enlace.
 * Se evaluo plegar midiendo el contenido con un `ResizeObserver` en vez de usar
 * un breakpoint, y se descarto: ningun tenant real lo necesita, y adentro del
 * iframe del preview del builder la entrega del observador va atada al ciclo de
 * fotogramas y no se pudo validar. El dia que un navbar pase los 1216px de
 * contenido, ningun breakpoint lo va a salvar y habra que volver a esa idea (o
 * sacarle algo a la barra).
 */
export const BuilderNavbarView = ({
  links = [
    { text: 'Inicio', url: '/' },
    { text: 'Financiamiento', url: '/financing' },
    { text: 'Consignaciones', url: '/consignments' },
    { text: 'Compra directa', url: '/buy-direct' },
    { text: 'Buscamos por ti', url: '/we-search-for-you' },
  ],
  ctaText = 'Contacto',
  ctaUrl = '/contact',
  bgColor = '#ffffff',
  textColor = '#4b5563',
  ctaBgColor = '',
  ctaTextColor = '#ffffff',
  logoUrl = '',
  logoHeight = 36,
  showLogo = true,
  sticky = true,
  transparentOnTop = true,
  fullWidth = false,
  isEditor = false,
  fixed = false,
  pathname: pathnameProp,
  innerRef,
}: BuilderNavbarViewProps) => {
  const { client } = useClientStore();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useThemeStore();

  /**
   * Las páginas de sistema que la automotora escondió desde el Builder
   * (`hiddenSystemPages`) devuelven 404: `BuilderPageWrapper` las corta, y el
   * sitemap y `llms.txt` ya las omitían. La barra era el único lugar que
   * seguía ofreciéndolas, así que un tenant que esconde una página y deja su
   * enlace —o que nunca tocó los enlaces y se quedó con los de fábrica, que
   * incluyen "Buscamos por ti"— publicaba un enlace a una página inexistente.
   *
   * Se filtra acá y no al guardar en el Builder a propósito: esconder y
   * mostrar una página es reversible, y reescribir los enlaces del navbar en
   * cada cambio haría perder los que la automotora configuró a mano.
   */
  const paginasOcultas = useMemo(
    () => getHiddenSystemPages(client?.client_website_config),
    [client?.client_website_config]
  );
  const linksVisibles = useMemo(() => {
    if (!paginasOcultas.length) return links;
    return links.filter((link) => {
      const slug = String(link?.url ?? '')
        .split(/[?#]/)[0]
        .replace(/^\/+|\/+$/g, '');
      // La home nunca se esconde; sin slug no hay nada que comparar.
      return slug ? !paginasOcultas.includes(slug) : true;
    });
  }, [links, paginasOcultas]);

  const [mobileOpen, setMobileOpen] = useState(false);

  // Atrás cierra el menú móvil en vez de abandonar la página. Mismo hook
  // que ya usan los paneles de filtros del listado.
  useCerrarConAtras(mobileOpen, () => setMobileOpen(false));
  // Submenú abierto (ticket #161). En escritorio lo gobierna el hover; en móvil,
  // el toque. `null` = ninguno, que es el estado de siempre.
  const [submenuAbierto, setSubmenuAbierto] = useState<number | null>(null);
  const [submenuMovil, setSubmenuMovil] = useState<number | null>(null);

  // Get current path safely (usePathname crashes inside CraftJS Frame). Cuando
  // lo renderiza el layout llega por prop, ya resuelto con usePathname.
  const [detectedPath, setDetectedPath] = useState('/');
  useEffect(() => {
    if (pathnameProp !== undefined) return;
    setDetectedPath(window.location.pathname);
    const onPop = () => setDetectedPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [pathnameProp]);
  useEffect(() => {
    if (pathnameProp !== undefined) return;
    setDetectedPath(window.location.pathname);
  });
  const pathname = pathnameProp ?? detectedPath;

  /**
   * El panel del menú ya no se cierra al tocar una opción: se cierra cuando la
   * ruta cambió de verdad.
   *
   * Cerrarlo en el mismo frame del toque parecía lo correcto, pero desmontaba
   * la fila tocada —y con ella el único lugar donde se puede avisar que el
   * toque entró—. El resultado era lo que reportaron Adrián y Seba: el menú
   * desaparece, queda la página anterior y no pasa nada durante uno o dos
   * segundos. Dejándolo abierto, la fila muestra su spinner todo ese rato
   * (ver `IndicadorDeNavegacion`).
   *
   * Dos salvedades. Tocar la página en la que ya estás no cambia la ruta, así
   * que ahí se cierra a mano. Y si la navegación se cuelga —la red se cae a
   * mitad de camino— este tope evita que el panel quede abierto para siempre.
   */
  const cierreDeSeguridad = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelarCierreTardio = () => {
    if (cierreDeSeguridad.current) { clearTimeout(cierreDeSeguridad.current); cierreDeSeguridad.current = null; }
  };
  useEffect(() => cancelarCierreTardio, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    cancelarCierreTardio();
  }, [pathname]);

  const alTocarOpcion = useCallback((href: string) => {
    if (href === pathname) { setMobileOpen(false); return; }
    cancelarCierreTardio();
    cierreDeSeguridad.current = setTimeout(() => setMobileOpen(false), 8000);
  }, [pathname]);

  // Scroll state — transparent at top, solid on scroll.
  // Solo se trackea cuando el efecto está habilitado y el navbar es sticky.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!sticky || !transparentOnTop || isEditor) return;
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sticky, transparentOnTop, isEditor]);

  const primaryColor = client?.theme?.light?.primary || '#e05d31';
  const finalCtaBgColor = ctaBgColor || primaryColor;
  const cta = resolveNavLink(ctaUrl);
  const companyName = client?.name || rubroDelPais(client?.country);
  const hasDarkMode = !!client?.has_dark_mode;
  const hasLanguageSelector = !!client?.has_language_selector;

  const isDarkBg = (() => {
    const c = bgColor.replace('#', '');
    if (c.length !== 6) return false;
    const r = parseInt(c.substring(0, 2), 16) / 255;
    const g = parseInt(c.substring(2, 4), 16) / 255;
    const b = parseInt(c.substring(4, 6), 16) / 255;
    return 0.299 * r + 0.587 * g + 0.114 * b < 0.4;
  })();
  // El logo elegido en el builder manda. Los de Configuración (client.logo /
  // logo_dark) son el fallback: antes ganaba logo_dark sobre fondo oscuro y el
  // logo del builder no se aplicaba nunca en navbars oscuros.
  const logoDark = client?.logo_dark || '';
  const useDarkLogo = isDarkBg || theme === 'dark';
  const finalLogoUrl = logoUrl || (useDarkLogo && logoDark ? logoDark : (client?.logo || ''));

  const effectiveBg = theme === 'dark' && !isDarkBg ? '#141414' : bgColor;
  const effectiveText = theme === 'dark' && !isDarkBg ? '#d4d4d4' : textColor;

  // En editor: siempre sólido. Si el efecto está apagado: siempre sólido.
  // Con efecto encendido: transparente arriba, sólido al scrollear.
  const showSolidBg = isEditor || !transparentOnTop || scrolled;

  // El link de la página actual se pinta con el color primario del tenant, que en
  // varias automotoras es EL MISMO color del navbar (Riesco: #1D4E89 sobre
  // #1D4E89; Mao Autos: negro sobre negro) → el link desaparecía. Si no contrasta
  // contra la barra, se marca con el color de texto normal + subrayado.
  const activeContrasts = contrastRatio(primaryColor, effectiveBg) >= 2.5;
  const activeColor = activeContrasts ? primaryColor : effectiveText;
  const activeUnderline = activeContrasts ? undefined : ('underline' as const);

  // Active route detection
  const isActiveLink = useCallback((url: string) => {
    if (url === '/') return pathname === '/';
    if (url.startsWith('#') || url.startsWith('/#')) return false;
    return pathname?.startsWith(url);
  }, [pathname]);

  /**
   * Opciones del submenú de un link (ticket #161). Devuelve `[]` cuando el link
   * no tiene, que es el caso de TODOS los navbars de hoy: ahí el link se dibuja
   * por el mismo camino de siempre.
   */
  const subOpciones = useCallback((link: NavLink): NavLink[] => {
    if (Array.isArray(link.children) && link.children.length) return link.children;
    return (link.submenu || '')
      .split('\n')
      .map((linea) => linea.trim())
      .filter(Boolean)
      .map((linea) => {
        const corte = linea.indexOf('|');
        if (corte < 0) return null;
        const text = linea.slice(0, corte).trim();
        const url = linea.slice(corte + 1).trim();
        return text && url ? { text, url } : null;
      })
      .filter((o): o is NavLink => o !== null);
  }, []);

  /**
   * Un link `#ancla` baja a esa sección de la misma página en vez de navegar
   * (ticket #161: "Semi nuevos" tiene que bajar al inicio del inventario).
   *
   * Reusa `navigateBuilderCta`, el mismo resolvedor que ya usan los botones de
   * los Heros, con su cadena de respaldo para encontrar la sección de vehículos
   * cuando el ancla exacta no existe. Dentro del editor no se intercepta nada.
   */
  const alTocarAncla = useCallback((e: React.MouseEvent, url: string) => {
    if (isEditor) return;
    // `#id` baja en la página actual. `/#id` es el que se configura en el menú:
    // desde una página interna navega a la home (y el navegador baja solo), y
    // estando YA en la home se comporta como el `#id` de arriba — si no, el
    // link no haría nada porque la ruta no cambia.
    const esAncla = url.startsWith('#');
    const esAnclaDeHome = url.startsWith('/#') && pathname === '/';
    if (!esAncla && !esAnclaDeHome) return;
    e.preventDefault();
    navigateBuilderCta(esAncla ? url : url.slice(1));
    setMobileOpen(false);
    setSubmenuAbierto(null);
    setSubmenuMovil(null);
  }, [isEditor, pathname]);

  const position = fixed ? 'fixed' : sticky ? 'sticky' : 'relative';

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (innerRef) innerRef(el); }}
      style={{
        backgroundColor: showSolidBg ? effectiveBg : 'transparent',
        position,
        top: position === 'relative' ? undefined : 0,
        left: fixed ? 0 : undefined,
        right: fixed ? 0 : undefined,
        zIndex: 100,
        backdropFilter: showSolidBg ? undefined : 'blur(12px)',
        boxShadow: showSolidBg ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
      }}
      className="w-full transition-all duration-300"
    >
      <div className={`${fullWidth ? 'max-w-full' : 'max-w-7xl'} mx-auto px-4 sm:px-6 lg:px-8`}>
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {showLogo && finalLogoUrl ? (
              <Link href="/">
                <Img loading="eager" variante="icono"
                  src={finalLogoUrl}
                  alt={companyName}
                  className="w-auto object-contain"
                  style={{ height: `${logoHeight}px` }}
                />
              </Link>
            ) : (
              <Link href="/" className="text-lg font-bold" style={{ color: effectiveText }}>
                {companyName}
              </Link>
            )}
          </div>

          {/* Enlaces de escritorio. El corte es `xl`: ver la nota de arriba. */}
          <div className="hidden xl:flex items-center gap-1">
            {linksVisibles.map((link, i) => {
              const { href, isExternal } = resolveNavLink(link.url);
              const linkClass = "px-3 py-2 text-sm font-medium rounded-md transition-colors hover:opacity-80 whitespace-nowrap";
              const active = isActiveLink(link.url);
              const linkStyle = {
                color: active ? activeColor : effectiveText,
                fontWeight: active ? 600 : 500,
                textDecoration: active ? activeUnderline : undefined,
                textUnderlineOffset: 6,
              };
              // Ticket #161: un link con submenú se dibuja como disparador de un
              // desplegable que se abre al pasar el mouse. Sin submenú —todos
              // los links de hoy— sigue el camino de siempre, intacto.
              const sub = subOpciones(link);
              if (sub.length > 0) {
                const abierto = submenuAbierto === i;
                return (
                  <div
                    key={i}
                    className="relative"
                    onMouseEnter={() => setSubmenuAbierto(i)}
                    onMouseLeave={() => setSubmenuAbierto((v) => (v === i ? null : v))}
                  >
                    <Link
                      href={href}
                      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      onClick={(e) => alTocarAncla(e, link.url)}
                      className={`${linkClass} inline-flex items-center gap-1`}
                      style={linkStyle}
                      aria-haspopup="true"
                      aria-expanded={abierto}
                    >
                      {link.text}
                      <ChevronIcon abierto={abierto} />
                    </Link>
                    {abierto && (
                      <div
                        className="absolute left-0 top-full z-50 min-w-[210px] rounded-xl border py-1 shadow-lg"
                        style={{
                          backgroundColor: effectiveBg,
                          borderColor: isDarkBg || theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                        }}
                        role="menu"
                      >
                        {sub.map((op, j) => {
                          const d = resolveNavLink(op.url);
                          const activa = isActiveLink(op.url);
                          const opClass = 'block px-4 py-2 text-sm whitespace-nowrap transition-colors hover:opacity-80';
                          const opStyle = {
                            color: activa ? activeColor : effectiveText,
                            fontWeight: activa ? 600 : 500,
                          };
                          return d.isExternal ? (
                            <a key={j} href={d.href} target="_blank" rel="noopener noreferrer" className={opClass} style={opStyle} role="menuitem">
                              {op.text}
                            </a>
                          ) : (
                            <Link
                              key={j}
                              href={d.href}
                              onClick={(e) => { alTocarAncla(e, op.url); setSubmenuAbierto(null); }}
                              className={opClass}
                              style={opStyle}
                              role="menuitem"
                            >
                              {op.text}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return isExternal ? (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={linkClass} style={linkStyle}>
                  {link.text}
                </a>
              ) : (
                <Link key={i} href={href} onClick={(e) => alTocarAncla(e, link.url)} className={linkClass} style={linkStyle}>
                  {link.text}
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {hasLanguageSelector && (
              <div className="hidden xl:block">
                <LanguageSelector variant="minimal" className="rounded-full" />
              </div>
            )}
            {hasDarkMode && (
              <button
                onClick={toggleTheme}
                className="hidden xl:block p-2 rounded-full transition-colors hover:opacity-80"
                style={{ color: effectiveText }}
                aria-label={t('site.nav.toggleTheme')}
              >
                {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              </button>
            )}
            {ctaText && (
              <Link
                href={cta.href}
                {...(cta.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="hidden xl:block px-4 py-2 text-sm font-medium rounded-md transition-colors hover:opacity-90"
                style={{
                  backgroundColor: finalCtaBgColor,
                  color: ctaTextColor,
                }}
              >
                {ctaText}
              </Link>
            )}

            {/* Mobile hamburger button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              /* `min-h-11 min-w-11` = 44px, el mínimo táctil de iOS. El ícono
                 mide lo mismo que antes: sólo crece el área que recibe el dedo,
                 que era de 36px (24 del ícono + `p-1.5`). */
              className="xl:hidden inline-flex items-center justify-center min-h-11 min-w-11 p-1.5 rounded-md transition-colors hover:opacity-80"
              aria-label={mobileOpen ? t('site.nav.menuClose') : t('site.nav.menuOpen')}
            >
              <MenuIcon open={mobileOpen} color={effectiveText} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          className="xl:hidden overflow-hidden"
          style={{ backgroundColor: effectiveBg }}
        >
          <div className="mx-3 mt-1 mb-3 rounded-2xl border overflow-hidden"
            style={{ borderColor: isDarkBg || theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
          >
            {/* Header with toggles */}
            {(hasLanguageSelector || hasDarkMode) && (
              <div className="flex items-center justify-between px-4 pt-3 pb-2">
                <div className="flex items-center gap-2">
                  {hasLanguageSelector && <LanguageSelector variant="minimal" className="rounded-full" />}
                  {hasDarkMode && (
                    <button
                      onClick={toggleTheme}
                      className="p-2 rounded-full transition-colors hover:opacity-80"
                      style={{ color: effectiveText }}
                      aria-label={t('site.nav.toggleTheme')}
                    >
                      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                    </button>
                  )}
                </div>
                <span className="text-xs truncate max-w-[50%]" style={{ color: effectiveText, opacity: 0.6 }}>
                  {companyName}
                </span>
              </div>
            )}

            {/* Nav links */}
            <ul className="px-1 py-1">
              {linksVisibles.map((link, i) => {
                const active = isActiveLink(link.url);
                const { href, isExternal } = resolveNavLink(link.url);
                // La fila es la misma para los dos casos; sólo cambia lo que va
                // a la derecha. En un link interno esa flecha pasa a ser un
                // spinner mientras la navegación está en curso —el toque tiene
                // que acusar recibo en el acto, sin esperar a la red. En uno
                // externo se va del sitio, así que no hay nada que indicar.
                const fila = (derecha: React.ReactNode) => (
                  <div className="flex items-center justify-between">
                    <span className="text-base">{link.text}</span>
                    {derecha}
                  </div>
                );
                const flechaQuieta = (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: active ? 1 : 0.4 }} aria-hidden="true">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                );
                const linkClass = "block w-full rounded-xl px-4 py-3 transition-colors";
                const linkStyle = {
                  backgroundColor: active ? `${activeColor}15` : 'transparent',
                  color: active ? activeColor : effectiveText,
                  fontWeight: active ? 600 : 400,
                  textDecoration: active ? activeUnderline : undefined,
                  textUnderlineOffset: 4,
                };
                // Ticket #161: en móvil el link con submenú NO navega — el toque
                // abre y cierra la lista de opciones, que se dibuja indentada
                // debajo. Sin submenú, la fila es exactamente la de antes.
                const sub = subOpciones(link);
                if (sub.length > 0) {
                  const abierto = submenuMovil === i;
                  return (
                    <li key={i} className="list-none">
                      {/* El texto navega a su propia página; el chevron, al lado,
                          es un botón aparte que sólo abre y cierra el submenú.
                          Así "Contacto" sigue llevando a /contact en el celular
                          (ticket #161). */}
                      <div className="flex items-stretch" style={{ backgroundColor: linkStyle.backgroundColor, borderRadius: 12 }}>
                        {isExternal ? (
                          <a href={href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className={`${linkClass} flex-1`} style={{ ...linkStyle, backgroundColor: 'transparent' }}>
                            <span className="text-base">{link.text}</span>
                          </a>
                        ) : (
                          <Link href={href} onClick={(e) => { alTocarAncla(e, link.url); alTocarOpcion(href); }} className={`${linkClass} flex-1`} style={{ ...linkStyle, backgroundColor: 'transparent' }}>
                            <span className="text-base">{link.text}</span>
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={() => setSubmenuMovil((v) => (v === i ? null : i))}
                          className="px-4 rounded-xl transition-colors"
                          style={{ color: linkStyle.color }}
                          aria-expanded={abierto}
                          aria-label={`${abierto ? '-' : '+'} ${link.text}`}
                        >
                          <ChevronIcon abierto={abierto} />
                        </button>
                      </div>
                      {abierto && (
                        <ul className="pl-3">
                          {sub.map((op, j) => {
                            const d = resolveNavLink(op.url);
                            const activa = isActiveLink(op.url);
                            const opStyle = {
                              backgroundColor: activa ? `${activeColor}15` : 'transparent',
                              color: activa ? activeColor : effectiveText,
                              fontWeight: activa ? 600 : 400,
                            };
                            const opFila = (derecha: React.ReactNode) => (
                              <div className="flex items-center justify-between">
                                <span className="text-[15px]">{op.text}</span>
                                {derecha}
                              </div>
                            );
                            return (
                              <li key={j} className="list-none">
                                {d.isExternal ? (
                                  <a href={d.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className={linkClass} style={opStyle}>
                                    {opFila(flechaQuieta)}
                                  </a>
                                ) : (
                                  <Link href={d.href} onClick={(e) => { alTocarAncla(e, op.url); alTocarOpcion(d.href); }} className={linkClass} style={opStyle}>
                                    {opFila(<IndicadorDeNavegacion opacidad={activa ? 1 : 0.4} />)}
                                  </Link>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }

                return (
                  <li key={i} className="list-none">
                    {isExternal ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className={linkClass} style={linkStyle}>
                        {fila(flechaQuieta)}
                      </a>
                    ) : (
                      <Link href={href} onClick={(e) => { alTocarAncla(e, link.url); alTocarOpcion(href); }} className={linkClass} style={linkStyle}>
                        {fila(<IndicadorDeNavegacion opacidad={active ? 1 : 0.4} />)}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* CTA button */}
            {ctaText && (
              <div className="p-3" style={{ borderTop: `1px solid ${isDarkBg || theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
                <Link
                  href={cta.href}
                  {...(cta.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  onClick={() => (cta.isExternal ? setMobileOpen(false) : alTocarOpcion(cta.href))}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium rounded-xl transition-colors hover:opacity-90"
                  style={{
                    backgroundColor: finalCtaBgColor,
                    color: ctaTextColor,
                  }}
                >
                  {ctaText}
                  {!cta.isExternal && <IndicadorDeNavegacion soloMientrasCarga />}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
